/* templateGenerators.js — the downloadable BLANK onboarding templates.
 *
 * Every blank is built from TEMPLATE_SPECS (templateSpecs.js). Each workbook has the PARSED data sheet FIRST
 * (header row + ISO month columns, NO data rows) so parseSingleSheet, which reads SheetNames[0], hits it; and a
 * separate "Instructions" sheet the parsers never read (they select their data sheet by name or position).
 * The Instructions text -- the legend, the notes, the signs and the worked example -- is ALL spec data, so the
 * blank, the parsers' recorded aliases and docs/ONBOARDING-TEMPLATES.md cannot drift apart: the drift gate
 * (scripts/gen-onboarding-spec.mjs --check) round-trips every worked example through its own parser.
 */
import * as XLSX from 'xlsx';
import { TEMPLATE_SPECS, specHeaders, specRequiredLabel, specValueLabels, buildHistoricalMonths } from './templateSpecs';
import { isOneMonthStart } from './companyWindow';   // the one-month start (2026-11-02)
import { PRE_LAUNCH_IS_ROWS, PRE_LAUNCH_BS_ROWS, PRE_LAUNCH_IS_NOTES, PRE_LAUNCH_BS_NOTES } from './preLaunchStarter';   // S1 (2026-11-02)

// The parsed data sheet: one header row (canonical names + ISO months) and no data -- or, for a Pre-Launch STARTER (S1,
// 2026-11-02), the starter rows with 0 in every month. With no rows the sheet is exactly the blank's.
function buildDataSheet(spec, months, rows) {
  if (!rows) return XLSX.utils.aoa_to_sheet([[...specHeaders(spec), ...months]]);
  return XLSX.utils.aoa_to_sheet([[...specHeaders(spec), ...months], ...rows.map(r => [...r, ...months.map(() => 0)])]);
}

// Append one spec's column legend, signs and worked example to an Instructions AOA.
function appendLegend(aoa, label, spec) {
  aoa.push([`${label} — tab "${spec.sheet}"`]);
  aoa.push(['Column', 'Required?', 'What to enter', 'Accepted values', 'If left blank']);
  spec.columns.forEach(c => aoa.push([c.name, specRequiredLabel(c), c.note || '', specValueLabels(c).join(' / '), c.blank || '']));
  if (spec.months && spec.months !== 'none') {
    aoa.push(['<month columns>', 'Required', 'One column per historical month, headed with the month as TEXT (YYYY-MM) -- already filled in for your period.']);
  }
  if (spec.signs && spec.signs.length) {
    aoa.push([]);
    aoa.push(['Signs:']);
    spec.signs.forEach(([what, sign]) => aoa.push([what, sign]));
  }
  aoa.push([]);
  aoa.push(['Worked example (for reference -- do NOT paste into the data tab):']);
  aoa.push([...specHeaders(spec), ...(spec.exampleMonths || [])]);
  (spec.examples || []).forEach(row => aoa.push([...row]));
  aoa.push([]);
}

// Build the "Instructions" sheet. `sections` = [{label, spec}]; `notes` = leading lines (the company name), then
// each section's own spec notes.
function buildInstructionsSheet(title, sections, notes = []) {
  const aoa = [[`${title} template — Instructions`], ['Fill in the data tab(s). Do not rename the columns or the tabs.'], []];
  const all = [...notes, ...sections.flatMap(s => s.spec.notes || [])];
  all.forEach(n => aoa.push([n]));
  if (all.length) aoa.push([]);
  sections.forEach(s => appendLegend(aoa, s.label, s.spec));
  return XLSX.utils.aoa_to_sheet(aoa);
}

function workbookWith(dataSheets, instructions) {
  const wb = XLSX.utils.book_new();
  dataSheets.forEach(({ name, sheet }) => XLSX.utils.book_append_sheet(wb, sheet, name)); // data FIRST
  XLSX.utils.book_append_sheet(wb, instructions, 'Instructions');
  return wb;
}

// ── IS ───────────────────────────────────────────────────────
export function generateISBlank(companyName, historicalStart, closeMonth) {
  const spec = TEMPLATE_SPECS.is;
  const months = buildHistoricalMonths(historicalStart, closeMonth, spec.months);
  const instr = buildInstructionsSheet('Income Statement', [{ label: 'Income Statement', spec }], [`Company: ${companyName}`]);
  return workbookWith([{ name: spec.sheet, sheet: buildDataSheet(spec, months) }], instr);
}

// S1 (2026-11-02) -- the PRE-LAUNCH STARTER income statement: the blank's layout and Instructions, plus the generic starter rows
// (utils/preLaunchStarter.js) with 0 in every month and the starter's own notes.
export function generateISStarter(companyName, historicalStart, closeMonth) {
  const spec = TEMPLATE_SPECS.is;
  const months = buildHistoricalMonths(historicalStart, closeMonth, spec.months);
  const instr = buildInstructionsSheet('Income Statement', [{ label: 'Income Statement', spec }], [`Company: ${companyName}`, ...PRE_LAUNCH_IS_NOTES]);
  return workbookWith([{ name: spec.sheet, sheet: buildDataSheet(spec, months, PRE_LAUNCH_IS_ROWS) }], instr);
}

// ── BS (opening column prepended) ────────────────────────────
export function generateBSBlank(companyName, historicalStart, closeMonth) { return bsWorkbook(companyName, historicalStart, closeMonth, false); }
// S1 (2026-11-02) -- the PRE-LAUNCH STARTER balance sheet: the blank's layout and Instructions (the one-month opening line
// included), plus the starter rows with 0 in every month and the starter's own notes.
export function generateBSStarter(companyName, historicalStart, closeMonth) { return bsWorkbook(companyName, historicalStart, closeMonth, true); }
function bsWorkbook(companyName, historicalStart, closeMonth, starter) {
  const spec = TEMPLATE_SPECS.bs;
  const months = buildHistoricalMonths(historicalStart, closeMonth, spec.months);
  /* 2026-11-02 (the one-month start, utils/companyWindow.js): a brand-new company's history is ONE month -- the month of its
   * opening balance sheet, which is its close month. Its blank has two columns: the month before (the company did not exist:
   * 0) and the close month (the opening balances). Every other start keeps the line below byte for byte. */
  const oneMonth = isOneMonthStart(historicalStart, closeMonth) && months.length === 2;
  const openingLine = oneMonth
    ? `Pre-Launch company: put your opening balances in the ${closeMonth} column (the month of your opening balance sheet); leave the ${months[0]} column at 0 (there was no balance sheet before the company started).`
    : `The FIRST month column (${months[0] || 'the month before your historical start'}) is the OPENING balance sheet — fill it in; it seeds your opening cash position.`;
  // The spec's own note ("The FIRST month column is the OPENING balance sheet ... it seeds the opening cash") is the general rule
  // and would contradict the line above, so a one-month start's blank leaves that ONE note out. Every other blank keeps it.
  const shownSpec = oneMonth ? { ...spec, notes: (spec.notes || []).filter(n => !n.startsWith('The FIRST month column is the OPENING balance sheet')) } : spec;
  const instr = buildInstructionsSheet('Balance Sheet', [{ label: 'Balance Sheet', spec: shownSpec }], [
    `Company: ${companyName}`,
    openingLine,
    ...(starter ? PRE_LAUNCH_BS_NOTES : []),
  ]);
  return workbookWith([{ name: spec.sheet, sheet: buildDataSheet(spec, months, starter ? PRE_LAUNCH_BS_ROWS : undefined) }], instr);
}

// ── Vendor + Prepaid (one combined workbook — Step 6 uploads one file) ───
export function generateVendorBlank(companyName, historicalStart, closeMonth) {
  const vSpec = TEMPLATE_SPECS.vendor, pSpec = TEMPLATE_SPECS.prepaid;
  const vMonths = buildHistoricalMonths(historicalStart, closeMonth, vSpec.months);
  const pMonths = buildHistoricalMonths(historicalStart, closeMonth, pSpec.months);
  const instr = buildInstructionsSheet('Vendors & Prepaid', [
    { label: 'Vendor monthly spend', spec: vSpec },
    { label: 'Prepaid contracts', spec: pSpec },
  ], [
    `Company: ${companyName}`,
    'This workbook has TWO data tabs: "Expenses" (vendor monthly spend) and "Prepaid" (prepaid contracts).',
  ]);
  return workbookWith([
    { name: vSpec.sheet, sheet: buildDataSheet(vSpec, vMonths) },   // "Expenses" first
    { name: pSpec.sheet, sheet: buildDataSheet(pSpec, pMonths) },   // "Prepaid"
  ], instr);
}

// ── Employee (no months) ─────────────────────────────────────
// NOTE: the raise instruction used to be written HERE and said raises were "NOT imported yet" -- false since the
// 2026-09-09 raise import (d68efd1 / 79afdc0). The notes now come from the spec (TEMPLATE_SPECS.employee.notes).
export function generateEmployeeBlank(companyName) {
  const spec = TEMPLATE_SPECS.employee;
  const instr = buildInstructionsSheet('Employees', [{ label: 'Employee roster', spec }], [`Company: ${companyName}`]);
  return workbookWith([{ name: spec.sheet, sheet: buildDataSheet(spec, []) }], instr);
}

// ── Asset (canonical — replaces the legacy D&A schedule blank) ───
export function generateAssetBlank(companyName) {
  const spec = TEMPLATE_SPECS.asset;
  const instr = buildInstructionsSheet('Assets', [{ label: 'Fixed & intangible assets', spec }], [
    `Company: ${companyName}`,
    'type is FA (fixed asset) or IA (intangible asset) — this decides depreciation vs amortization.',
    'bs_line must be one of YOUR Balance Sheet asset lines (defined in Step 4) — e.g. "Computer Equipment". The import validates it and will not guess.',
  ]);
  return workbookWith([{ name: spec.sheet, sheet: buildDataSheet(spec, []) }], instr);
}

// -- Vendor forecasts (no months) -- uploaded on Step 6's Forecast sub-step (2026-10-29) ---
export function generateVendorForecastBlank(companyName) {
  const spec = TEMPLATE_SPECS.vendor_forecast;
  const instr = buildInstructionsSheet('Vendor forecasts', [{ label: 'Vendor forecasts', spec }], [`Company: ${companyName}`]);
  return workbookWith([{ name: spec.sheet, sheet: buildDataSheet(spec, []) }], instr);
}

// ── Download wrappers ────────────────────────────────────────
export function downloadISBlank(companyName, hs, cm) { XLSX.writeFile(generateISBlank(companyName, hs, cm), 'IS_Template.xlsx'); }
export function downloadBSBlank(companyName, hs, cm) { XLSX.writeFile(generateBSBlank(companyName, hs, cm), 'BS_Template.xlsx'); }
export function downloadISStarter(companyName, hs, cm) { XLSX.writeFile(generateISStarter(companyName, hs, cm), 'IS_PreLaunch_Starter.xlsx'); }   // S1
export function downloadBSStarter(companyName, hs, cm) { XLSX.writeFile(generateBSStarter(companyName, hs, cm), 'BS_PreLaunch_Starter.xlsx'); }   // S1
export function downloadVendorBlank(companyName, hs, cm) { XLSX.writeFile(generateVendorBlank(companyName, hs, cm), 'Vendor_Template.xlsx'); }
export function downloadEmployeeBlank(companyName) { XLSX.writeFile(generateEmployeeBlank(companyName), 'Employee_Template.xlsx'); }
export function downloadAssetBlank(companyName) { XLSX.writeFile(generateAssetBlank(companyName), 'Asset_Template.xlsx'); }
export function downloadVendorForecastBlank(companyName) { XLSX.writeFile(generateVendorForecastBlank(companyName), 'Vendor_Forecast_Template.xlsx'); }
